import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import {
    Box, Typography, Container, Paper, Grid, Button, CircularProgress, Alert,
    Fade, Grow, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
    AccountBalance, Receipt, OpenInNew,
    TrendingUp, TrendingDown, CalendarMonth, AttachMoney, AccountBalanceWallet, ShowChart,
} from '@mui/icons-material';
import { D, MONTHS, fmt, selectMenuProps } from '../constants/plaidDashboardTheme';
import { dedupePlaidLinkedAccounts } from '../utils/plaidAccountDisplay';

function PlaidAccounts() {
    const [year, setYear] = useState(new Date().getFullYear());
    const [institutions, setInstitutions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);

    const loadData = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get('/api/plaid/institutions'),
            api.get('/api/plaid/summary', { params: { year } })
        ]).then(([instRes, sumRes]) => {
            setInstitutions(instRes.data || []);
            setSummary(sumRes.data);
        }).catch(console.error).finally(() => setLoading(false));
    }, [year]);

    useEffect(() => { loadData(); }, [loadData]);

    const displayInstitutions = useMemo(() => {
        const byName = new Map();
        for (const inst of institutions) {
            const key = (inst.name || '').trim().toLowerCase() || `_id_${inst.id}`;
            const cur = byName.get(key);
            if (!cur) {
                byName.set(key, {
                    name: inst.name,
                    accounts: [...(inst.accounts || [])],
                    connectionIds: [inst.id],
                });
            } else {
                cur.connectionIds.push(inst.id);
                const seenIds = new Set(cur.accounts.map((a) => a.id));
                for (const a of inst.accounts || []) {
                    if (!seenIds.has(a.id)) {
                        seenIds.add(a.id);
                        cur.accounts.push(a);
                    }
                }
            }
        }
        return Array.from(byName.values()).map((cur) => ({
            ...cur,
            accounts: dedupePlaidLinkedAccounts(
                (cur.accounts || []).map((a) => ({ ...a, institutionName: cur.name })),
            ),
        }));
    }, [institutions]);

    const displayAccountCount = useMemo(
        () => displayInstitutions.reduce((total, inst) => total + (inst.accounts?.length || 0), 0),
        [displayInstitutions],
    );

    /** Closed Select field: theme defaults to white paper — force dark surface + light text. */
    const selectFieldSx = {
        '& .MuiInputBase-root': {
            backgroundColor: D.card,
            color: D.fg,
            borderRadius: 2,
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: D.card,
            color: D.fg,
        },
        '& .MuiSelect-select': {
            color: `${D.fg} !important`,
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: D.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: D.muted },
        '& .MuiInputLabel-root': { color: D.muted },
        '& .MuiInputLabel-root.Mui-focused': { color: D.primary },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': { backgroundColor: 'transparent' },
        '& .MuiSvgIcon-root': { color: D.muted },
    };

    if (loading) {
        return (
            <Box sx={{
                minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: D.bg,
            }}>
                <CircularProgress size={40} sx={{ color: D.primary }} />
            </Box>
        );
    }

    const txCount = summary?.transactionCount || 0;
    const totalSpend = parseFloat(summary?.totalSpend || 0);
    const totalIncome = parseFloat(summary?.totalIncome || 0);
    const byMonth = summary?.byMonth || {};
    const byCategory = summary?.byCategory || {};
    const byAccount = summary?.byAccount || {};
    const netFlow = totalIncome - totalSpend;
    const isNetPositive = netFlow >= 0;
    const volume = totalSpend + totalIncome;
    const flowPct = volume > 0 ? ((Math.abs(netFlow) / volume) * 100).toFixed(1) : '0';

    const bc = summary?.byClassification || {};
    const businessNet = parseFloat(bc.businessNet ?? 0);
    const personalNet = parseFloat(bc.personalNet ?? 0);
    const unclassifiedCount = bc.unclassifiedCount ?? 0;
    const businessIncomeL = parseFloat(bc.businessIncome ?? 0);
    const businessSpendL = parseFloat(bc.businessSpend ?? 0);
    const personalIncomeL = parseFloat(bc.personalIncome ?? 0);
    const personalSpendL = parseFloat(bc.personalSpend ?? 0);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: D.bg, color: D.fg }}>
            {/* Header — finance-dashboard style */}
            <Box sx={{ borderBottom: `1px solid ${D.border}`, bgcolor: D.cardHeaderBg }}>
                <Container maxWidth="lg" sx={{ maxWidth: '1280px !important', px: { xs: 2, sm: 3 }, py: 2 }}>
                    <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Box sx={{
                                width: 40, height: 40, borderRadius: 2, bgcolor: D.primary,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <AccountBalance sx={{ color: D.primaryFg, fontSize: 22 }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: D.fg, lineHeight: 1.3 }}>
                                    Accounts & cash flow
                                </Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: D.muted }}>
                                    Link personal and business accounts to see monthly income and expenses together
                                </Typography>
                            </Box>
                        </Box>
                        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                            <Button
                                variant="contained"
                                component={Link}
                                to={`/account-transactions?year=${year}&month=${new Date().getMonth() + 1}`}
                                startIcon={<Receipt sx={{ fontSize: 20 }} />}
                                sx={{
                                    bgcolor: D.primary,
                                    color: D.primaryFg,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    fontSize: '0.95rem',
                                    py: 1.1,
                                    px: 2,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 20px rgba(115,135,255,0.35)',
                                    '&:hover': { bgcolor: '#8b9cff' },
                                }}
                            >
                                Tag transactions
                            </Button>
                            <Box display="flex" alignItems="center" gap={1} sx={{ color: D.muted }}>
                                <CalendarMonth sx={{ fontSize: 18 }} />
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>Period:</Typography>
                            </Box>
                            <FormControl size="small" sx={{ minWidth: 120, ...selectFieldSx }}>
                                <InputLabel id="plaid-year-lbl">Year</InputLabel>
                                <Select labelId="plaid-year-lbl" label="Year" value={year}
                                    onChange={e => setYear(e.target.value)} MenuProps={selectMenuProps}
                                    sx={{ borderRadius: 2 }}>
                                    {[2024, 2025, 2026, 2027].map(y => (
                                        <MenuItem key={y} value={y}>{y}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ maxWidth: '1280px !important', px: { xs: 2, sm: 3 }, py: 3 }}>
                {alert && (
                    <Fade in>
                        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{
                            mb: 2, borderRadius: 2,
                            bgcolor: alert.severity === 'error' ? 'rgba(248,113,113,0.12)' : 'rgba(74,222,128,0.12)',
                            color: D.fg,
                            border: `1px solid ${alert.severity === 'error' ? 'rgba(248,113,113,0.35)' : 'rgba(74,222,128,0.35)'}`,
                            '& .MuiAlert-icon': { color: alert.severity === 'error' ? '#f87171' : D.success },
                        }}>
                            {alert.message}
                        </Alert>
                    </Fade>
                )}

                {institutions.length === 0 ? (
                    <Paper elevation={0} sx={{
                        p: 6, borderRadius: 3, textAlign: 'center',
                        bgcolor: D.card, border: `1px solid ${D.border}`,
                    }}>
                        <AccountBalance sx={{ fontSize: 56, color: D.muted, mb: 2 }} />
                        <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: D.fg, mb: 1 }}>
                            No accounts linked yet
                        </Typography>
                        <Typography sx={{ fontSize: '0.875rem', color: D.muted, mb: 2 }}>
                            Link banks in Settings → Connections, then return here to see cash flow and transactions.
                        </Typography>
                        <Button variant="contained" component={Link} to="/settings"
                            sx={{
                                bgcolor: D.primary, color: D.primaryFg, fontWeight: 600, textTransform: 'none',
                                borderRadius: 2, boxShadow: 'none',
                                '&:hover': { bgcolor: '#8b9cff', boxShadow: 'none' },
                            }}>
                            Open Settings — Connections
                        </Button>
                    </Paper>
                ) : (
                    <Box display="flex" flexDirection="column" gap={3}>
                        {/* Hero — owner-profit-card style */}
                        <Paper elevation={0} sx={{
                            borderRadius: 3,
                            border: `2px solid ${D.primary}44`,
                            background: `linear-gradient(135deg, ${D.card} 0%, ${D.card} 55%, rgba(115,135,255,0.08) 100%)`,
                            overflow: 'hidden',
                        }}>
                            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: D.muted, mb: 0.5 }}>
                                    Combined cash flow ({year})
                                </Typography>
                                <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} alignItems={{ lg: 'center' }} justifyContent="space-between" gap={3}>
                                    <Box>
                                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                            <Typography sx={{
                                                fontSize: { xs: '2rem', sm: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.02em',
                                                color: isNetPositive ? D.success : D.spend,
                                            }}>
                                                {isNetPositive ? '+' : '−'}${fmt(Math.abs(netFlow))}
                                            </Typography>
                                            <Box sx={{
                                                width: 40, height: 40, borderRadius: '50%',
                                                bgcolor: isNetPositive ? D.successBg : D.spendBg,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                {isNetPositive
                                                    ? <TrendingUp sx={{ color: D.success, fontSize: 22 }} />
                                                    : <TrendingDown sx={{ color: D.spend, fontSize: 22 }} />}
                                            </Box>
                                        </Box>
                                        <Typography sx={{ fontSize: '0.875rem', color: D.muted, mt: 0.5 }}>
                                            Income minus expenses across all linked personal and business accounts
                                        </Typography>
                                    </Box>
                                    <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                                        <Box sx={{ px: 2, py: 1.5, borderRadius: 2, bgcolor: D.secondary, textAlign: 'center', minWidth: 100 }}>
                                            <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>Expenses</Typography>
                                            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: D.spend }}>
                                                ${fmt(totalSpend)}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ color: D.muted, display: { xs: 'none', sm: 'block' } }}>→</Typography>
                                        <Box sx={{ px: 2, py: 1.5, borderRadius: 2, bgcolor: D.secondary, textAlign: 'center', minWidth: 100 }}>
                                            <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>Income</Typography>
                                            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: D.income }}>
                                                ${fmt(totalIncome)}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ color: D.muted, display: { xs: 'none', sm: 'block' } }}>→</Typography>
                                        <Box sx={{
                                            px: 2, py: 1.5, borderRadius: 2, textAlign: 'center', minWidth: 100,
                                            bgcolor: isNetPositive ? D.successBg : D.spendBg,
                                        }}>
                                            <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>Net</Typography>
                                            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: isNetPositive ? D.success : D.spend }}>
                                                {isNetPositive ? '+' : ''}${fmt(netFlow)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{
                                    mt: 3, pt: 2, borderTop: `1px solid ${D.border}`,
                                    display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2,
                                }}>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>Transactions</Typography>
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: D.fg }}>{txCount}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>Flow balance</Typography>
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: D.fg }}>{flowPct}%</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>Banks</Typography>
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: D.fg }}>{displayInstitutions.length}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>Accounts</Typography>
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: D.fg }}>{displayAccountCount}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: D.card, border: `1px solid ${D.border}` }}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: D.fg, mb: 0.5 }}>
                                Business vs personal (your labels)
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: D.muted, mb: 2 }}>
                                Mark each transaction below as Business or Personal so books stay accurate when a business account has mixed charges.
                                Totals here include only rows you have labeled.
                            </Typography>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                                gap: 2,
                            }}>
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: D.secondary, border: `1px solid ${D.border}` }}>
                                    <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>Business net ({year})</Typography>
                                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: businessNet >= 0 ? D.success : D.spend }}>
                                        {businessNet >= 0 ? '+' : '−'}${fmt(Math.abs(businessNet))}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.65rem', color: D.muted, mt: 0.5 }}>
                                        Inc ${fmt(businessIncomeL)} · Exp ${fmt(businessSpendL)}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: D.secondary, border: `1px solid ${D.border}` }}>
                                    <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>Personal net ({year})</Typography>
                                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: personalNet >= 0 ? D.success : D.spend }}>
                                        {personalNet >= 0 ? '+' : '−'}${fmt(Math.abs(personalNet))}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.65rem', color: D.muted, mt: 0.5 }}>
                                        Inc ${fmt(personalIncomeL)} · Exp ${fmt(personalSpendL)}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: D.secondary, border: `1px solid ${D.border}` }}>
                                    <Typography sx={{ fontSize: '0.7rem', color: D.muted }}>Unclassified txns</Typography>
                                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: D.fg }}>{unclassifiedCount}</Typography>
                                    <Typography sx={{ fontSize: '0.65rem', color: D.muted, mt: 0.5 }}>Still in combined totals above</Typography>
                                </Box>
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: D.secondary, border: `1px solid ${D.border}`, display: 'flex', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: D.muted, lineHeight: 1.4 }}>
                                        Use the{' '}
                                        <Typography component="span" sx={{ color: D.fg, fontWeight: 700 }}>Books</Typography>
                                        {' '}column in transactions to label each row.
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Summary cards — summary-cards.tsx style */}
                        <Grid container spacing={2}>
                            {[
                                { label: 'Total expenses', sub: 'Personal & business, year to date', value: `$${fmt(totalSpend)}`, icon: AttachMoney, iconColor: D.spend },
                                { label: 'Total income', sub: 'All linked accounts, year to date', value: `$${fmt(totalIncome)}`, icon: AccountBalanceWallet, iconColor: D.income },
                                { label: 'Transactions', sub: 'Imported for this year', value: txCount, icon: Receipt, iconColor: D.chartPrimary },
                                { label: 'Banks linked', sub: 'Where accounts are connected', value: displayInstitutions.length, icon: ShowChart, iconColor: D.chartPrimary },
                            ].map((card, i) => {
                                const StatIcon = card.icon;
                                return (
                                <Grid item xs={12} sm={6} lg={3} key={card.label}>
                                    <Grow in timeout={350 + i * 80}>
                                        <Paper elevation={0} sx={{
                                            p: 2, borderRadius: 3, height: '100%',
                                            bgcolor: D.card, border: `1px solid ${D.border}`,
                                        }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" pb={1}>
                                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: D.muted }}>
                                                    {card.label}
                                                </Typography>
                                                <StatIcon sx={{ fontSize: 18, color: card.iconColor }} />
                                            </Box>
                                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: D.fg }}>
                                                {card.value}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: D.muted, mt: 0.5 }}>{card.sub}</Typography>
                                        </Paper>
                                    </Grow>
                                </Grid>
                                );
                            })}
                        </Grid>

                        <Paper elevation={0} sx={{
                            mb: 2, p: 2, borderRadius: 2,
                            border: `1px solid ${D.border}`,
                            bgcolor: D.secondary,
                            display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                        }}>
                            <Typography sx={{ fontSize: '0.85rem', color: D.muted }}>
                                Manage linked banks, add accounts, and sync in{' '}
                                <Typography component={Link} to="/settings" sx={{ color: D.primary, fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    Settings → Connections
                                </Typography>
                                .
                            </Typography>
                        </Paper>

                        <Grid container spacing={3}>
                            <Grid item xs={12} xl={7}>
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: D.card, border: `1px solid ${D.border}` }}>
                                    <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: D.fg, mb: 0.5 }}>Monthly activity</Typography>
                                    <Typography sx={{ fontSize: '0.875rem', color: D.muted, mb: 2 }}>
                                        Combined income and expense volume by month for {year}
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ '& th': { borderColor: D.border } }}>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: D.muted, bgcolor: 'transparent' }}>Month</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: D.muted, bgcolor: 'transparent' }}>Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(byMonth).map(([m, amt]) => {
                                                    const val = parseFloat(amt);
                                                    if (val === 0) return null;
                                                    return (
                                                        <TableRow key={m} hover sx={{
                                                            '&:hover': { bgcolor: D.secondary },
                                                            '& td': { borderColor: D.border, color: D.fg },
                                                        }}>
                                                            <TableCell sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{MONTHS[parseInt(m) - 1]}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', color: D.income }}>${fmt(val)}</TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} xl={5}>
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: D.card, border: `1px solid ${D.border}`, mb: 3 }}>
                                    <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: D.fg, mb: 0.5 }}>By category</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', color: D.muted, mb: 2 }}>
                                        Spending mix across all linked accounts
                                    </Typography>
                                    {Object.entries(byCategory)
                                        .sort(([, a], [, b]) => parseFloat(b) - parseFloat(a))
                                        .map(([cat, amt], i) => {
                                            const val = parseFloat(amt);
                                            const pct = volume > 0 ? (val / volume) * 100 : 0;
                                            return (
                                                <Box key={i} sx={{ mb: 1.5 }}>
                                                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: D.fg }}>{cat}</Typography>
                                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: D.fg }}>
                                                            ${fmt(val)}{' '}
                                                            <Typography component="span" sx={{ fontSize: '0.7rem', color: D.muted }}>
                                                                ({pct.toFixed(1)}%)
                                                            </Typography>
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ height: 4, borderRadius: 2, bgcolor: D.secondary }}>
                                                        <Box sx={{
                                                            height: '100%', width: `${Math.min(pct, 100)}%`, borderRadius: 2,
                                                            background: `linear-gradient(90deg, ${D.primary}, #a78bfa)`,
                                                        }} />
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                </Paper>
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: D.card, border: `1px solid ${D.border}` }}>
                                    <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: D.fg, mb: 0.5 }}>By account</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', color: D.muted, mb: 2 }}>
                                        Personal and business accounts side by side
                                    </Typography>
                                    {Object.entries(byAccount)
                                        .sort(([, a], [, b]) => parseFloat(b) - parseFloat(a))
                                        .map(([acct, amt], i) => (
                                            <Box key={i} display="flex" justifyContent="space-between" sx={{
                                                py: 1, borderBottom: `1px solid ${D.border}`,
                                                '&:last-child': { borderBottom: 0 },
                                            }}>
                                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: D.fg }}>{acct}</Typography>
                                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: D.fg }}>${fmt(parseFloat(amt))}</Typography>
                                            </Box>
                                        ))}
                                </Paper>
                            </Grid>
                        </Grid>

                        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${D.border}`, bgcolor: D.card }}>
                            <Box
                                component={Link}
                                to={`/account-transactions?year=${year}&month=${new Date().getMonth() + 1}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2.5,
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    bgcolor: D.secondary,
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': { bgcolor: D.secondaryStrong },
                                }}
                            >
                                <Receipt sx={{ fontSize: 28, color: D.primary }} />
                                <Box flex={1}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: D.fg }}>
                                        Open transactions workspace
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.875rem', color: D.muted }}>
                                        Full-screen layout with larger text and dropdowns for Books and My category
                                    </Typography>
                                </Box>
                                <OpenInNew sx={{ color: D.muted, fontSize: 22 }} />
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Container>
        </Box>
    );
}

export default PlaidAccounts;
